import type { LineData, UTCTimestamp, WhitespaceData } from 'lightweight-charts'

import { parseProbability } from '@/features/data/components/widgets/content/fedwatch/fedwatch-format'
import { DATA_CHART_FEDWATCH_PALETTE } from '@/features/data/lib/chart-colors'
import { isMeetingDate } from '@/service/fedwatch.types'
import type {
  FedwatchProbabilityList,
  FedwatchProbabilityPoint,
} from '@/types/generated/public_api'

/** A point that survived validation, with its strings already parsed. */
interface ValidPoint {
  timestampMs: number
  meetingDate: string
  meetingMs: number
  targetLowerBp: number
  cumulativeMoveBp: number
  probability: number
}

/**
 * Validation is per point, and only a field the whole row depends on can discard
 * one. A single unusable cell never takes its siblings down with it.
 *
 * `meetingDate` gets the round-trip check rather than a regex: it drives both the
 * row sort and `meetingMs`, and `/^\d{4}-\d{2}-\d{2}$/` happily accepts `2026-13-40`.
 *
 * `cumulativeMoveBp` is validated as strictly as `targetLowerBp` because it feeds
 * `standingLowerBp` below — a NaN or a stray fraction there would poison the mode
 * that produces the "current target range" label for the entire grid.
 */
function toValidPoint(point: FedwatchProbabilityPoint): ValidPoint | null {
  const timestampMs = Number(point.timestampMs)
  if (!Number.isFinite(timestampMs)) return null

  if (!isMeetingDate(point.meetingDate)) return null
  const meetingMs = Date.parse(`${point.meetingDate}T00:00:00Z`)

  if (!Number.isInteger(point.targetLowerBp)) return null
  if (!Number.isInteger(point.cumulativeMoveBp)) return null

  const probability = parseProbability(point.probability)
  if (probability === null) return null

  return {
    timestampMs,
    meetingDate: point.meetingDate,
    meetingMs,
    targetLowerBp: point.targetLowerBp,
    cumulativeMoveBp: point.cumulativeMoveBp,
    probability,
  }
}

// ─── Grid ─────────────────────────────────────────────────────────────────────

export interface FedwatchGridRow {
  meetingDate: string
  meetingMs: number
  /** The newest timestamp seen for THIS meeting. */
  timestampMs: number
  /** targetLowerBp → probability. An ABSENT key means "not priced", never zero. */
  cells: Map<number, number>
  /** Highest-probability outcome, for highlighting. */
  modeBp: number | null
}

export interface FedwatchGrid {
  rows: FedwatchGridRow[]
  /** Ascending union of every targetLowerBp across all meetings. */
  columns: number[]
  asOfMs: number | null
  /** The target range standing today, derived — see below. */
  standingLowerBp: number | null
  standingInconsistent: boolean
}

const EMPTY_GRID: FedwatchGrid = {
  rows: [],
  columns: [],
  asOfMs: null,
  standingLowerBp: null,
  standingInconsistent: false,
}

/**
 * Response → the probability grid: meetings down, target ranges across.
 *
 * The grid is deliberately **ragged**. Later meetings carry more cells than earlier
 * ones because the rate tree fans out, so `cells` is a Map rather than a dense
 * array — the renderer is then forced to branch on `has()`, and an unpriced outcome
 * renders as absent instead of as a confident 0.0%.
 *
 * Probabilities are unconditional and already sum to one per meeting. They are
 * never renormalised here: re-dividing would silently repair a producer bug and
 * change the numbers under the user.
 */
export function normalizeFedwatchGrid(list: FedwatchProbabilityList | undefined): FedwatchGrid {
  const points = (list?.items ?? []).flatMap((point) => {
    const valid = toValidPoint(point)
    return valid ? [valid] : []
  })
  if (points.length === 0) return EMPTY_GRID

  // Newest-per-meeting, not newest-overall. A snapshot is normally one uniform
  // timestamp, but a stream replacement or a partially restamped grid must not
  // blend two vintages into a single row — and a meeting that legitimately lags
  // should keep its own older row rather than disappear.
  const maxTsByMeeting = new Map<string, number>()
  for (const point of points) {
    const current = maxTsByMeeting.get(point.meetingDate)
    if (current === undefined || point.timestampMs > current) {
      maxTsByMeeting.set(point.meetingDate, point.timestampMs)
    }
  }

  const rowsByMeeting = new Map<string, FedwatchGridRow>()
  const columns = new Set<number>()
  const standingCounts = new Map<number, number>()
  let asOfMs: number | null = null

  for (const point of points) {
    if (point.timestampMs !== maxTsByMeeting.get(point.meetingDate)) continue

    let row = rowsByMeeting.get(point.meetingDate)
    if (!row) {
      row = {
        meetingDate: point.meetingDate,
        meetingMs: point.meetingMs,
        timestampMs: point.timestampMs,
        cells: new Map(),
        modeBp: null,
      }
      rowsByMeeting.set(point.meetingDate, row)
    }

    // Duplicates at the same (meeting, bp, ts) resolve last-wins. Never summed —
    // adding them would double-count and break the sum-to-one invariant.
    row.cells.set(point.targetLowerBp, point.probability)
    columns.add(point.targetLowerBp)

    // target = standing + move, so standing = target - move. Constant across a
    // well-formed grid, which makes it both the label source and the cheapest
    // invariant we have. Cuts work out: 375 - (-25) = 400.
    const standing = point.targetLowerBp - point.cumulativeMoveBp
    standingCounts.set(standing, (standingCounts.get(standing) ?? 0) + 1)

    if (asOfMs === null || point.timestampMs > asOfMs) asOfMs = point.timestampMs
  }

  for (const row of rowsByMeeting.values()) {
    let modeBp: number | null = null
    let best = -Infinity
    for (const [bp, probability] of row.cells) {
      if (probability > best) {
        best = probability
        modeBp = bp
      }
    }
    row.modeBp = modeBp
  }

  let standingLowerBp: number | null = null
  let bestCount = -1
  for (const [standing, count] of standingCounts) {
    if (count > bestCount) {
      bestCount = count
      standingLowerBp = standing
    }
  }

  return {
    rows: [...rowsByMeeting.values()].sort((a, b) => a.meetingMs - b.meetingMs),
    columns: [...columns].sort((a, b) => a - b),
    asOfMs,
    standingLowerBp,
    // More than one distinct value means the producer restamped a grid mid-flight.
    standingInconsistent: standingCounts.size > 1,
  }
}

// ─── Timeline ─────────────────────────────────────────────────────────────────

export const FEDWATCH_PALETTE_LENGTH: number = DATA_CHART_FEDWATCH_PALETTE.length
const DEFAULT_MAX_SERIES = FEDWATCH_PALETTE_LENGTH
const DEFAULT_MIN_PROBABILITY = 0.0005
const GAP_FACTOR = 2

/**
 * Colour is a function of the basis-point value itself, never of the series'
 * position in the list.
 *
 * Assigning by sorted index would recolour every line the moment a new lower range
 * entered the tree — 325 appearing below 350 shifts all the indices after it. The
 * ranges sit on a 25bp lattice, so dividing by 25 gives a stable, deterministic
 * index in which adjacent ranges also land on adjacent palette entries.
 */
export function colorIndexForBp(bp: number, paletteLength = FEDWATCH_PALETTE_LENGTH): number {
  return Math.abs(Math.floor(bp / 25)) % paletteLength
}

export interface FedwatchTimelineSeries {
  targetLowerBp: number
  colorIndex: number
  data: Array<LineData<UTCTimestamp> | WhitespaceData<UTCTimestamp>>
  maxProbability: number
}

export interface FedwatchTimeline {
  series: FedwatchTimelineSeries[]
  /** The shared axis: ascending, unique, real times plus synthetic gap markers. */
  times: UTCTimestamp[]
  /** Observed cadence in seconds; null when there are too few points to tell. */
  cadenceSec: number | null
  gapCount: number
}

const EMPTY_TIMELINE: FedwatchTimeline = {
  series: [],
  times: [],
  cadenceSec: null,
  gapCount: 0,
}

/** Lower median: for an even count this biases to the shorter interval, so a run
 * of `[cadence, gap]` reports the cadence rather than splitting the difference. */
function lowerMedian(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.floor((sorted.length - 1) / 2)]
}

export interface FedwatchTimelineOptions {
  maxSeries?: number
  minProbability?: number
  paletteLength?: number
}

/**
 * Response → one line per target range for a single meeting.
 *
 * The series has genuine holes: publishing stops whenever CME is shut, nightly and
 * from Friday afternoon to Sunday evening. Rather than hardcode those hours or the
 * server's downsampling table, the expected cadence is **derived from the data** as
 * the median gap between consecutive points, and anything more than twice that is
 * treated as a hole. That self-calibrates across the 1m/15m/1h/1d bucket sizes the
 * server picks by span.
 *
 * A hole is marked with a single value-less point rather than a dense fill: a
 * weekend at one-minute cadence would otherwise be ~3,700 phantom points per
 * series, which both costs memory and squeezes the real data into a sliver of the
 * time axis.
 */
export function normalizeFedwatchTimeline(
  list: FedwatchProbabilityList | undefined,
  meetingDate: string,
  options: FedwatchTimelineOptions = {}
): FedwatchTimeline {
  const {
    maxSeries = DEFAULT_MAX_SERIES,
    minProbability = DEFAULT_MIN_PROBABILITY,
    paletteLength = FEDWATCH_PALETTE_LENGTH,
  } = options

  const points = (list?.items ?? []).flatMap((point) => {
    const valid = toValidPoint(point)
    return valid && valid.meetingDate === meetingDate ? [valid] : []
  })
  if (points.length === 0) return EMPTY_TIMELINE

  // Bucketed to whole seconds up front — lightweight-charts' UTCTimestamp is
  // seconds, so collapsing here guarantees the strictly-ascending-unique axis it
  // requires instead of discovering a collision at setData time.
  const bySecond = new Map<number, Map<number, number>>()
  const maxByBp = new Map<number, number>()
  for (const point of points) {
    const second = Math.floor(point.timestampMs / 1000)
    let row = bySecond.get(second)
    if (!row) {
      row = new Map()
      bySecond.set(second, row)
    }
    row.set(point.targetLowerBp, point.probability)

    const currentMax = maxByBp.get(point.targetLowerBp) ?? -Infinity
    if (point.probability > currentMax) maxByBp.set(point.targetLowerBp, point.probability)
  }

  const realTimes = [...bySecond.keys()].sort((a, b) => a - b)

  // With two points a cadence and a gap are indistinguishable, so claim neither.
  const deltas: number[] = []
  for (let i = 1; i < realTimes.length; i += 1) deltas.push(realTimes[i] - realTimes[i - 1])
  const cadenceSec = realTimes.length >= 3 ? lowerMedian(deltas) : null

  const syntheticTimes: number[] = []
  if (cadenceSec !== null && cadenceSec > 0) {
    const threshold = GAP_FACTOR * cadenceSec
    for (let i = 1; i < realTimes.length; i += 1) {
      // Exclusive: at a 60s cadence a single dropped bucket gives 120s, which is
      // noise and must not break the line. 121s is a real hole.
      if (realTimes[i] - realTimes[i - 1] > threshold) {
        syntheticTimes.push(realTimes[i - 1] + cadenceSec)
      }
    }
  }

  const times = [...realTimes, ...syntheticTimes].sort((a, b) => a - b) as UTCTimestamp[]

  const keptBps = [...maxByBp.entries()]
    .filter(([, max]) => max >= minProbability)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxSeries)
    .map(([bp]) => bp)
    .sort((a, b) => a - b)

  const series = keptBps.map((bp) => ({
    targetLowerBp: bp,
    colorIndex: colorIndexForBp(bp, paletteLength),
    maxProbability: maxByBp.get(bp) ?? 0,
    // Every series spans the same axis. A range that was not in the tree at an
    // instant emits a value-less point, exactly as a market closure does — both are
    // "unpriced", and neither is probability zero, so both must break the line
    // rather than let it join across.
    data: times.map((time) => {
      const value = bySecond.get(time as number)?.get(bp)
      return value === undefined ? { time } : { time, value }
    }),
  }))

  return { series, times, cadenceSec, gapCount: syntheticTimes.length }
}
